import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { placeScheduleWithoutVisitOnly } from "../functions/handleScheduleData.functions";
import { useRemoteFetchAdminLocations } from "../../../hooks/Remote/Location/useRemoteFetchAdminLocations";
import { useRemoteFetchNoPlaceZoneBookingSchedule } from "../../../hooks/Remote/Schedule/useRemoteFetchNoPlaceZoneBookingSchedule";
import { useRemoteFetchPlaceSchedule } from "../../../hooks/Remote/Schedule/useRemoteFetchPlaceSchedule";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { BookableBookingObjectDto } from "../typings/resource-calendar.types";
import {
  filterBookableObjectsByLocationIds,
  resolveLocationInventoryIdForResourcePrefill
} from "../utils/schedule-resource-location.utils";

export type ResourceLocationCard = {
  id: number;
  name: string;
  picturePath: string | null;
};

export type UseScheduleResourceLocationFilterArgs = {
  /** True when standalone resource booking (`BookingType.RESOURCE`) is active. */
  enabled: boolean;
  userId: string;
  workplaceBookingEnabled?: boolean;
  /** Conference / room schedule fetch (noplacezone). */
  hasConferenceLicense?: boolean;
  /**
   * When set, used for prefill instead of fetching today's workplace/room schedules.
   * May include workplace and conference bookings.
   */
  prefillSchedules?: BookingScheduleInterface[];
};

function getTodayScheduleRange() {
  const today = DateTime.now().toFormat("yyyy-MM-dd");
  return {
    startDate: today,
    endDate: DateTime.fromISO(today).plus({ days: 1 }).toFormat("yyyy-MM-dd")
  };
}

export function useScheduleResourceLocationFilter({
  enabled,
  userId,
  workplaceBookingEnabled = true,
  hasConferenceLicense = false,
  prefillSchedules
}: Readonly<UseScheduleResourceLocationFilterArgs>) {
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const userHasInteractedRef = useRef(false);
  const todayRange = useMemo(() => getTodayScheduleRange(), []);

  const shouldFetchTodaySchedules = enabled && prefillSchedules == null;
  const shouldFetchTodayWorkplace = shouldFetchTodaySchedules && workplaceBookingEnabled;
  const shouldFetchTodayConference = shouldFetchTodaySchedules && hasConferenceLicense;

  const bookingScheduleDto = useMemo(
    () => ({
      userIds: [userId],
      startDate: todayRange.startDate,
      endDate: todayRange.endDate
    }),
    [todayRange.endDate, todayRange.startDate, userId]
  );

  const { data: todayPlaceScheduleData } = useRemoteFetchPlaceSchedule({
    bookingScheduleDto,
    placeScheduleEnabled: shouldFetchTodayWorkplace
  });

  const { data: todayConferenceScheduleData } = useRemoteFetchNoPlaceZoneBookingSchedule({
    bookingScheduleDto,
    hasNoPlaceZoneLicense: shouldFetchTodayConference
  });

  const schedulesForPrefill = useMemo(() => {
    if (prefillSchedules != null) {
      return prefillSchedules;
    }

    const placeSchedules = placeScheduleWithoutVisitOnly(todayPlaceScheduleData);
    const conferenceSchedules = todayConferenceScheduleData ?? [];
    return [...placeSchedules, ...conferenceSchedules];
  }, [prefillSchedules, todayConferenceScheduleData, todayPlaceScheduleData]);

  const { data: locations } = useRemoteFetchAdminLocations({
    mode: "admin",
    enabled
  });

  const applyPrefill = useCallback(() => {
    const locationId = resolveLocationInventoryIdForResourcePrefill(schedulesForPrefill, userId);
    if (locationId != null) {
      setSelectedLocationIds([locationId]);
    }
  }, [schedulesForPrefill, userId]);

  useEffect(() => {
    if (!enabled) {
      userHasInteractedRef.current = false;
      setSelectedLocationIds([]);
      return;
    }

    userHasInteractedRef.current = false;
    applyPrefill();
  }, [applyPrefill, enabled]);

  useEffect(() => {
    if (!enabled || userHasInteractedRef.current || selectedLocationIds.length > 0) {
      return;
    }

    applyPrefill();
  }, [applyPrefill, enabled, selectedLocationIds.length, schedulesForPrefill]);

  const toggleResourceLocation = useCallback((locationId: number) => {
    userHasInteractedRef.current = true;
    setSelectedLocationIds(previous =>
      previous.includes(locationId)
        ? previous.filter(id => id !== locationId)
        : [...previous, locationId]
    );
  }, []);

  const clearResourceLocationFilter = useCallback(() => {
    userHasInteractedRef.current = true;
    setSelectedLocationIds([]);
  }, []);

  const resourceLocationCards: ResourceLocationCard[] = useMemo(
    () =>
      (locations ?? []).map(location => ({
        id: location.id,
        name: location.name,
        picturePath: location.picturePath ?? null
      })),
    [locations]
  );

  const filterBookableObjects = useCallback(
    (objects: BookableBookingObjectDto[]) =>
      filterBookableObjectsByLocationIds(objects, selectedLocationIds),
    [selectedLocationIds]
  );

  return {
    resourceLocationCards,
    selectedResourceLocationIds: selectedLocationIds,
    toggleResourceLocation,
    clearResourceLocationFilter,
    filterBookableObjects
  };
}
